import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ComponentCard from "../../../components/common/ComponentCard";
import Label from "../../../components/form/Label";
import Input from "../../../components/form/input/InputField";
import Checkbox from "../../../components/form/input/Checkbox";
import Button from "../../../components/ui/button/Button";
import Form from "../../../components/form/Form";

const apiUrl = process.env.REACT_APP_API_URL;



type SurveyMode = "create" | "view";

export default function AddNewSurvey() {
  const location = useLocation();
  const surveyToView = location.state?.survey;
  const [mode] = useState<SurveyMode>(surveyToView ? "view" : "create");
  const [errors, setErrors] = useState<{ contactNumber?: string; customerName?: string; email?: string}>({});

  // Convert database values to your form format
  const convertFromDatabaseFormat = (value: number): string => {
    if (value === 1) return "Yes";
    if (value === 2) return "No";
    return "N/A";
  };

  const queryParams = new URLSearchParams(window.location.search);
  const syscoregroupid = queryParams.get('groupid');
  const user_id = queryParams.get('userid');
  const cst = queryParams.get('cst');
  const qrcode = queryParams.get('qr_code');

  // console.log(syscoregroupid, user_id, cst);

  // Initialize formData with surveyToView if it exists
  const [formData, setFormData] = useState({
    customerName: surveyToView?.customername || "",
    memberNumber: surveyToView?.memnumber?.toString() || "",
    contactNumber: surveyToView?.phone?.toString() || "",
    email: surveyToView?.email || "",
    venue: surveyToView?.venue || "",
    tableNumber: surveyToView?.tablename?.toString() || "",
    staffName: surveyToView?.staffname || "",
    additionalFeedback: surveyToView?.feedback || "",
    greeted: convertFromDatabaseFormat(surveyToView?.greeted) || "N/A",
    greetedComment: surveyToView?.greetedrem || "",
    enjoyMeal: convertFromDatabaseFormat(surveyToView?.enjoymeal) || "N/A",
    enjoyMealComment: surveyToView?.enjoymealrem || "",
    foodTemp: convertFromDatabaseFormat(surveyToView?.foodtemp) || "N/A",
    foodTempComment: surveyToView?.foodtemprem || "",
    bevService: convertFromDatabaseFormat(surveyToView?.bevservice) || "N/A",
    bevServiceComment: surveyToView?.bevservicerem || "",
    bevTime: convertFromDatabaseFormat(surveyToView?.bevtime) || "N/A",
    bevTimeComment: surveyToView?.bevtimerem || "",
    menuSel: convertFromDatabaseFormat(surveyToView?.menusel) || "N/A",
    menuSelComment: surveyToView?.menuselrem || "",
    dinedBefore: convertFromDatabaseFormat(surveyToView?.dinedbefore) || "N/A",
    dinedBeforeComment: surveyToView?.dinedbeforerem || "",
    returnRest: convertFromDatabaseFormat(surveyToView?.returnrest) || "N/A",
    returnRestComment: surveyToView?.returnrestrem || "",
  });


  const payload: { [key: string]: any } = { ...formData };
  if (syscoregroupid) {
    payload.syscoregroupid = syscoregroupid;
  }
  if (user_id) {
    payload.user_id = user_id;
  }
  if (cst) {
    payload.cst = cst;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (mode === "view") return;
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (fieldName: string, value: string) => {
    setFormData(prev => ({ 
      ...prev, 
      [fieldName]: value,
      [`${fieldName}Comment`]: "" // Reset comment when answer changes
    }));
  };

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    // console.log("Form submitted:", formData);
    e.preventDefault();
    if (mode === "view") return;

    const newErrors: {
    contactNumber?: string;
    customerName?: string;
    email?: string;
  } = {};

  // Validate each required field
  if (!formData.contactNumber.trim()) {
    newErrors.contactNumber = "Contact Number name is required";
  }
  if (!formData.customerName.trim()) {
    newErrors.customerName = "Customer name is required";
  }
  if (!formData.email.trim()) {
    newErrors.email = "Email number is required";
  }

  // If there are errors, set and scroll to the first
  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);

    // Scroll to the first invalid input
    if (newErrors.contactNumber) {
      const input = document.getElementById("staff_name");
      input?.scrollIntoView({ behavior: "smooth" });
      input?.focus();
    } else if (newErrors.customerName) {
      const input = document.getElementById("customer_name");
      input?.scrollIntoView({ behavior: "smooth" });
      input?.focus();
    } else if (newErrors.email) {
      const input = document.getElementById("table");
      input?.scrollIntoView({ behavior: "smooth" });
      input?.focus();
    }

    return;
  }

  // Clear errors if all validations pass
  setErrors({});

    try{
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/customer_surveys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        // if form is submitted through QR code URL
        if (queryParams.get("qr_code")) {
          alert("Thank you for submitting the survey!");
        }else{
          // if Form is submitted through web app
          navigate("/customer-surveys");
        }
        
      }
    }catch(err){
      console.log("Error:", err);
      
    }
  };

  const handleCancel = () => {
  if (qrcode) {
    // Set background to blank (e.g., white) before showing alert
    document.body.style.background = "#fff";
    document.body.innerHTML = ""; // optional: clears the visible page content

    // Delay the alert slightly to let the UI update
    setTimeout(() => {
      alert("Survey cancelled. Thank you for your time!");
    }, 50);
  } else {
    navigate("/customer-surveys");
  }
};

  const isDisabled = mode === "view";
  const renderCheckboxOrDisplay = (fieldName: string, question: string) => {
    const value = formData[fieldName as keyof typeof formData] as string;
    const comment = formData[`${fieldName}Comment` as keyof typeof formData] as string;

    return isDisabled ? (
      <div className="mt-2">
        <div className="flex items-center">
          <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-md mr-2">
            {value}
          </span>
          {comment && (
            // <span className="text-gray-600 dark:text-gray-400">
            //   - {comment}
            // </span>
            <textarea
              className={`w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none ${
                isDisabled
                  ? "bg-gray-100 dark:bg-gray-800 cursor-default"
                  : "bg-gray-50 dark:bg-gray-800 bg-transparent"
              } text-gray-600 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800.`} value={comment}
            />
          )}
        </div>
      </div>
    ) : (
      <>
        <div className="flex items-center gap-4 mt-2 mb-10">
          {["N/A", "Yes", "No"].map((option) => (
            <Checkbox
              key={option}
              checked={value === option}
              onChange={() => handleCheckboxChange(fieldName, option)}
              label={option}
            />
          ))}
        </div>
        {(value === "Yes" || value === "No") && (
          <div className="mt-4">
            <textarea
              className="w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none bg-gray-50 dark:bg-gray-800 bg-transparent text-gray-600 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800."
              name={`${fieldName}Comment`}
              placeholder="Add a comment..."
              value={comment}
              onChange={handleChange}
              disabled={isDisabled}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <ComponentCard title={mode === "view" ? "Survey Details" : "Add New Survey"}>
      <Form onSubmit={handleSubmit}>
        <>
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="col-span-full">
              <h4 className="pb-4 text-base font-medium text-gray-800 border-b border-gray-200 dark:border-gray-800 dark:text-white/90">
                Customer Info
              </h4>
            </div>
            <div className="">
              <Label htmlFor="customerName">Customer Name</Label>                                                               
              <Input 
                type="text" 
                id="customer_name" 
                name="customerName" 
                value={formData.customerName} 
                onChange={handleChange}
                disabled={isDisabled}
                className={`${isDisabled ? "bg-gray-100 dark:bg-gray-800" : ""} ${errors.customerName ? "border-red-500" : ""}`} 
              />
              {errors.customerName && (
                <p className="text-sm text-red-500 mt-1">{errors.customerName}</p>
              )}
            </div>
            <div className="">
              <Label htmlFor="memberNumbe">Member Number</Label>
              <Input 
                type="number" 
                id="member_number" 
                name="memberNumber" 
                value={formData.memberNumber}
                onChange={handleChange}
                disabled={isDisabled}
                className={isDisabled ? "bg-gray-100 dark:bg-gray-800" : ""} 
              />
            </div>
            <div className="">
              <Label htmlFor="table">Table</Label>
              <Input 
                type="number" 
                id="table" 
                name="tableNumber" 
                value={formData.tableNumber} 
                onChange={handleChange}
                disabled={isDisabled}
                className={`${isDisabled ? "bg-gray-100 dark:bg-gray-800" : ""}`}
              />
              
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange}
                disabled={isDisabled}
                className={`${isDisabled ? "bg-gray-100 dark:bg-gray-800" : ""} ${errors.email ? "border-red-500" : ""}`}  
              />
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email}</p>
              )}    
            </div>
            <div className="">
              <Label htmlFor="contactNumber">Contact Number</Label>
              <Input 
                type="number" 
                id="contact_number" 
                name="contactNumber" 
                value={formData.contactNumber} 
                onChange={handleChange}
                disabled={isDisabled}
                className={isDisabled ? "bg-gray-100 dark:bg-gray-800" : ""}  
              />
              {errors.contactNumber && (
                <p className="text-sm text-red-500 mt-1">{errors.contactNumber}</p>
              )}
            </div>
            <div className="">
              <Label htmlFor="staffName">Staff Name</Label>
              <Input 
                type="text" 
                id="staff_name" 
                name="staffName" 
                value={formData.staffName} 
                onChange={handleChange}
                disabled={isDisabled}
                className={`${isDisabled ? "bg-gray-100 dark:bg-gray-800 cursor-default" : ""}`}
              />
            </div>
          </div>
          <div className="col-span-2">
            <h4 className="pb-4 text-base font-medium text-gray-800 border-b border-gray-200 dark:border-gray-800 dark:text-white/90 mb-8 mt-8">
              Survey Info
            </h4>
          </div>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="p-4 space-y-4">
              <div>
                <Label className="flex items-center mt-4">
                  Were you greeted in a friendly and informative way at reception?
                </Label>
                {renderCheckboxOrDisplay("greeted", "greetedComment")}
              </div>

              <div>
                <Label className="flex items-center mt-4">
                  Did you enjoy your meal and beverage?
                </Label>
                {renderCheckboxOrDisplay("enjoyMeal", "enjoyMealComment")}
              </div>

              <div>
                <Label className="flex items-center mt-4">
                  Was your food served at the right temperature?
                </Label>
                
                {renderCheckboxOrDisplay("foodTemp", "foodTempComment")}
              </div>

              <div>
                <Label className="flex items-center mt-4">
                  Did your overall food and beverage service meet your expectations?
                </Label>    
                {renderCheckboxOrDisplay("bevService", "bevServiceComment")}
              </div>

              <div>
                <Label className="flex items-center mt-4">
                  Were your food and beverages served in an acceptable time?
                </Label>
                {renderCheckboxOrDisplay("bevTime", "bevTimeComment")}
              </div>

              <div>
                <Label className="flex items-center mt-4">
                  Were you happy with the food and beverage menu selection?
                </Label>
                {renderCheckboxOrDisplay("menuSel", "menuSelComment")}
              </div>

              <div>
                <Label className="flex items-center mt-4">
                  Have you visited the venue previously?
                </Label>
                
                {renderCheckboxOrDisplay("dinedBefore", "dinedBeforeComment")}
              </div>

              <div>
                <Label className="flex items-center mt-4">
                  Are you likely to return to the venue to wine or dine?
                </Label>
              
                {renderCheckboxOrDisplay("returnRest", "returnRestComment")}
              </div>
            </div>
          </div>
          <div className="col-span-2 mb-8 mt-8">
            <Label>Additional Feedback or Suggestions</Label>
            <textarea
              className={`w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none ${
                isDisabled
                  ? "bg-gray-100 dark:bg-gray-800 cursor-default"
                  : "bg-gray-50 dark:bg-gray-800 bg-transparent"
              } text-gray-600 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800.`} 
              name="additionalFeedback" 
              value={formData.additionalFeedback} 
              onChange={handleChange}
              disabled={isDisabled}
            />
          </div>
          <div className="flex gap-3">
            {mode === "view" ? (
              <Button size="sm" variant="outline" onClick={handleCancel}>
                Back to List
              </Button>
            ) : (
              <>
                <Button size="sm">Save Survey</Button>
                <Button size="sm" variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </>
            )}
          </div>
        </>
      </Form>
    </ComponentCard>
  );
}